import { Dialog, DialogContent, DialogTitle } from '@/components/Dialog';
import { useDimensions } from '@/hooks/useDimensions';
import { cn } from '@/utils';
import { DialogDescription } from '@radix-ui/react-dialog';
import { useCallback, useEffect, useMemo } from 'react';
import { create } from 'zustand';
import NewStyleFilter from '../components/NewStyleFilter';
import { NotchedBackground } from '../components/NotchedBackground';
import { useGeneratorFilterStore } from '@/state/generator-filter-store';
import { CloseFilledIcon } from '@/components/Icons';
import { useAudioPlayerStore } from '@/state/audio-player-store';
import { toast } from 'sonner';
import JenErrorToast from '@/components/ui/jen-error-toast';
import { FilterOrderRequest, getStyleFilterGroupList } from '@/service/filter';
import { useRequest } from 'ahooks';

interface FilterDialogStore {
  open: boolean;
  disabled: boolean;
  setOpen: (open: boolean) => void;
  setDisabled: (disabled: boolean) => void;
}

const useFilterDialogStore = create<FilterDialogStore>((set) => ({
  open: false,
  disabled: false,
  setOpen: (open) => set({ open }),
  setDisabled: (disabled) => set({ disabled }),
}));

export const useStyleFilter = () => {
  const { open, disabled, setOpen, setDisabled } = useFilterDialogStore();
  const { isMobileOrPad, isIPhoneSEorSmaller } = useDimensions();

  const { filterList, filterGroupList, filterListError, filterGroupListError, submitFilterOrder, setFilterGroupList } =
    useGeneratorFilterStore();

  const { run: fetchFilterGroupList } = useRequest(() => getStyleFilterGroupList(true), {
    onSuccess: (data) => {
      setFilterGroupList(data.data?.filter((fg) => fg.artist));
    },
    cacheKey: 'fetchFilterGroupList',
  });

  const { pause } = useAudioPlayerStore((state) => ({
    pause: state.pause,
  }));

  const show = useCallback(() => {
    setOpen(true);
  }, [setOpen]);

  const hide = useCallback(() => {
    const currentPlayAudioInfo = useAudioPlayerStore.getState().playAudioInfo;
    const currentIsPlaying = useAudioPlayerStore.getState().isPlaying;

    // the track in track list's length is 36
    if (currentIsPlaying && currentPlayAudioInfo?.id?.length !== 36) {
      pause();
    }
    setOpen(false);
  }, [pause, setOpen]);

  const postStyleFilterOrderRun = async (req: FilterOrderRequest) => {
    try {
      await submitFilterOrder(req);
    } catch (error) {
      toast(<JenErrorToast error={new Error(error instanceof Error ? error.message : 'Unknown error')} />, {
        className: '!border-0 !shadow-none',
        unstyled: true,
      });
    }
  };

  const postStyleFilterOrderRunAsync = submitFilterOrder;

  const StyleFilterDialogComponent = useMemo(
    () =>
      function FilterDialog() {
        return (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogContent
              closeClassName="hidden"
              closePosition="right"
              className={cn(
                ' shadow-none fixed left-[50%] top-[50%] z-50 grid w-[668px] max-w-none translate-x-[-50%] translate-y-[-50%] bg-background p-6  duration-200 text-white data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg bg-transparent',
                isMobileOrPad ? 'w-full h-screen p-2' : 'h-[550px]',
                isIPhoneSEorSmaller && 'scale-[0.9]',
              )}
            >
              <div
                className=" cursor-pointer absolute gap-[6px] top-[-60px] left-[350px] transform -translate-x-1/2  flex text-[#fff] bg-[#2b2d2f] p-[12px] rounded-full "
                onClick={hide}
              >
                <CloseFilledIcon fillColor="currentColor" /> Close
              </div>
              <NotchedBackground width={isMobileOrPad ? '100%' : '668px'} height={isMobileOrPad ? '100%' : '550px'} tagLeft="610px">
                <NewStyleFilter hide={hide} />
              </NotchedBackground>
            </DialogContent>
            <DialogDescription />
          </Dialog>
        );
      },
    [open, disabled, isIPhoneSEorSmaller, hide],
  );

  return {
    getStyleFilterListData: { data: filterList, error: filterListError },
    open,
    disabled,
    show,
    hide,
    setDisabled,
    StyleFilterDialogComponent,
    postStyleFilterOrderRun,
    postStyleFilterOrderRunAsync,
    getStyleFilterGroupListData: { data: filterGroupList, error: filterGroupListError },
  };
};
